/**
 * RedirectIfAuthenticated — the inverse of `RequireAuth`, now capability-aware
 * across the three persona doors (Track-0 persona-auth, spec D6a).
 *
 * Keeps an already-signed-in user OFF the auth / onboarding screens. WHERE they
 * land depends on the door the route belongs to:
 *
 *   • consumer, blind fast path (`/signup`, `/verify-email`, and any other
 *     onboarding route that doesn't pass `identityAware`): a /me round-trip is
 *     pointless there — those screens have nothing to reject, so ANY
 *     authenticated identity (including admin, who may freely act as a regular
 *     user per SCRUM-1xx) redirects straight to `/chat` the instant the token
 *     lands. NO /me fetch, no extra render states.
 *
 *   • consumer, identity-aware (`/login` passes `identityAware`): SCRUM-1xx —
 *     an admin account must be REJECTED at the consumer login form, not
 *     silently authenticated. `matchesPersona("consumer")` is no longer always
 *     true (an admin identity fails it), so this route needs the same
 *     identity-resolution dance as seller/admin below — the blind fast path
 *     would race ahead of `AuthLoginScreen.onSubmit`'s own /me-driven
 *     reject-and-logout flow, bouncing the user to /chat and back before the
 *     wrong-door message can ever render.
 *
 *   • seller / admin: the door belongs to a *capability* (owns a live shop /
 *     holds an admin permission), not to "any authenticated user". Redirecting
 *     the instant `setAccessToken` fires would bounce a NON-matching user (e.g.
 *     a non-seller at /seller/login) into a guard they cannot pass (→403)
 *     BEFORE the login screen can show its inline wrong-door CTA — violating the
 *     explicit AC. So we resolve the persona by identity first:
 *       – a safe same-origin `return_to` still wins immediately (no /me);
 *       – otherwise we read the shared `["me"]` identity — the SAME query
 *         `AuthLoginScreen.onSubmit` and the route guards use, never a divergent
 *         refetch. While it is in flight we render the CHILDREN (not a spinner,
 *         not a bounce): the login screen stays MOUNTED so its in-flight submit
 *         and `wrongDoor` state survive. A spinner here would remount the screen
 *         and discard both — re-introducing the bug. Once resolved: a matching
 *         identity redirects to the door; a non-matching one renders the
 *         children so `AuthLoginScreen` owns the inline wrong-door CTA.
 *
 * Boot UX (unchanged): the access token lives in JS memory only, so a hard
 * reload starts with `accessToken: null`. While the one-shot boot refresh
 * (`bootstrapAuth`) is still in flight we show the shared pending spinner rather
 * than flashing the form to a user who actually has a valid HttpOnly refresh
 * cookie — once it resolves we either redirect (session restored) or render the
 * form (genuinely logged out).
 */
import type { ReactElement, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useSearchParams } from "react-router-dom";

import { AuthBootstrapPending } from "@/app/RequireAuth";
import { getMe } from "@/features/account/api";
import {
  DOOR_DEFAULT,
  matchesPersona,
  safeReturnToOrNull,
  type AuthPersona,
  type PersonaIdentity,
} from "@/features/auth/destination";
import {
  selectAuthBootstrapped,
  selectIsAuthenticated,
  useAuthStore,
} from "@/lib/auth-store";

export interface RedirectIfAuthenticatedProps {
  children: ReactNode;
  /** The door this route belongs to. Defaults to the consumer dashboard. */
  persona?: AuthPersona;
  /**
   * Opt the consumer persona into identity-based resolution instead of the
   * blind "any token -> /chat" fast path. Set on `/login` only (SCRUM-1xx) —
   * see the module doc comment. Ignored for seller/admin, which always
   * resolve by identity.
   */
  identityAware?: boolean;
}

export function RedirectIfAuthenticated({
  children,
  persona = "consumer",
  identityAware = false,
}: RedirectIfAuthenticatedProps): ReactElement {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const bootstrapped = useAuthStore(selectAuthBootstrapped);
  const [params] = useSearchParams();

  // A safe same-origin return_to wins for every persona (Task 2 resolver rule).
  const safeReturnTo = safeReturnToOrNull(params.get("return_to"));

  // The blind fast path only applies to the consumer persona's non-identity-
  // aware routes (/signup, /verify-email, …). Every other case — seller,
  // admin, and consumer routes that opted in via `identityAware` — resolves
  // by /me. Same ["me"] cache key + queryFn as `AuthLoginScreen.onSubmit` and
  // the route guards (`useMe`) — a shared read, never a divergent refetch.
  const resolveByIdentity = persona !== "consumer" || identityAware;
  const needsIdentity = isAuthenticated && resolveByIdentity && safeReturnTo === null;
  const { data: profile, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    staleTime: 60_000,
    enabled: needsIdentity,
  });

  if (isAuthenticated) {
    if (safeReturnTo !== null) {
      return <Navigate to={safeReturnTo} replace />;
    }
    // Blind consumer fast path: no /me round-trip, redirect immediately.
    if (!resolveByIdentity) {
      return <Navigate to={DOOR_DEFAULT[persona]} replace />;
    }
    // Identity-aware (seller / admin / identity-aware consumer): resolve by
    // identity. While /me is in flight, render the children so the login
    // screen stays mounted (do NOT bounce, do NOT remount via a spinner) —
    // `AuthLoginScreen.onSubmit`, running the same `["me"]` query, owns the
    // inline wrong-door CTA on the live instance.
    if (isLoading || profile === undefined) {
      return <>{children}</>;
    }
    const identity: PersonaIdentity = {
      permissions: profile.permissions,
      is_seller: profile.is_seller,
    };
    if (matchesPersona(persona, identity)) {
      return <Navigate to={DOOR_DEFAULT[persona]} replace />;
    }
    // Wrong door: render the children so `AuthLoginScreen` shows the inline CTA.
    return <>{children}</>;
  }
  if (!bootstrapped) {
    return <AuthBootstrapPending />;
  }
  return <>{children}</>;
}

export default RedirectIfAuthenticated;
